import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { useAuthentication } from '@/hooks/useAuthentication';
import { wagmiConfig } from '@/lib/wagmi';
import { estimateGas } from '@/utils/gas';
import { absBigInt, formatTokenAmount } from '@onbeam/utils';
import { useEffect, useState } from 'react';
import { isAddress } from 'viem';
import { estimateContractGas, getBalance } from 'viem/actions';

type Options = {
  multiplier?: number;
  isEnabled?: boolean;
};

export const useEstimateGas = (
  params: Parameters<typeof estimateContractGas>[1],
  options: Options = {},
) => {
  const { userAddress } = useAuthentication();
  const marketplaceChain = useMarketplaceChain();
  const [gasAfterTx, setGasAfterTx] = useState<bigint>(0n);

  useEffect(() => {
    if (!marketplaceChain.gasBalanceCheckEnabled || options.isEnabled === false) return;
    if (!userAddress || !isAddress(userAddress)) return;

    (async () => {
      try {
        const gasFee = await estimateGas(userAddress, params);

        const totalGasFee = gasFee.wei * BigInt(options.multiplier || 1);

        const balance = await getBalance(wagmiConfig.getClient({ chainId: marketplaceChain.id }), {
          address: userAddress,
        });

        setGasAfterTx(balance - totalGasFee);
      } catch (e) {
        console.error('Error estimating gas:', e);
      }
    })();
  }, [marketplaceChain.gasBalanceCheckEnabled, marketplaceChain.id, options, params, userAddress]);

  const hasEnoughFunds = gasAfterTx >= 0n;

  return {
    hasEnoughFunds,
    showGasWarning: !hasEnoughFunds && marketplaceChain.gasBalanceCheckEnabled,
    deficit: formatTokenAmount(absBigInt(gasAfterTx), {
      maximumFractionDigits: 4,
    }),
  };
};
